import React from 'react';
import { Mail, FileText, Share2, Instagram, Facebook, Linkedin, CheckCircle, Clock, AlertCircle } from 'lucide-react';

interface ActivityItem {
  id: string;
  type: 'blog' | 'campaign' | 'social';
  title: string;
  date: Date;
  status: string;
  platform?: string;
  imageUrl?: string;
}

interface ActivityWeekViewProps {
  currentDate: Date;
  items: ActivityItem[];
}

const TYPE_CONFIG = {
  blog: { icon: FileText, gradient: 'from-emerald-500 to-teal-600' },
  campaign: { icon: Mail, gradient: 'from-sky-500 to-blue-600' },
  social: { icon: Share2, gradient: 'from-amber-400 to-orange-500' },
};

function getPlatformIcon(platform?: string) {
  if (platform?.toLowerCase().includes('instagram')) return Instagram;
  if (platform?.toLowerCase().includes('facebook')) return Facebook;
  if (platform?.toLowerCase().includes('linkedin')) return Linkedin;
  return Share2;
}

function getStatusIcon(status: string) {
  if (status === 'published' || status === 'completed' || status === 'sent') {
    return <CheckCircle className="w-3 h-3 text-emerald-500" />;
  } else if (status === 'scheduled') {
    return <Clock className="w-3 h-3 text-sky-500" />;
  } else if (status === 'failed') {
    return <AlertCircle className="w-3 h-3 text-red-500" />;
  }
  return <Clock className="w-3 h-3 text-gray-400" />;
}

export function ActivityWeekView({ currentDate, items }: ActivityWeekViewProps) {
  const startOfWeek = new Date(currentDate);
  startOfWeek.setDate(startOfWeek.getDate() - (startOfWeek.getDay() === 0 ? 6 : startOfWeek.getDay() - 1));
  startOfWeek.setHours(0, 0, 0, 0);

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const day = new Date(startOfWeek);
    day.setDate(day.getDate() + i);
    return day;
  });

  const getItemsForDay = (day: Date) => {
    return items.filter(item => {
      const itemDate = new Date(item.date);
      return itemDate.getDate() === day.getDate() &&
             itemDate.getMonth() === day.getMonth() &&
             itemDate.getFullYear() === day.getFullYear();
    }).sort((a, b) => a.date.getTime() - b.date.getTime());
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-x-auto">
      <div className="grid grid-cols-7 gap-px bg-gray-100 min-w-[800px]">
        {weekDays.map((day, idx) => {
          const dayItems = getItemsForDay(day);
          const isToday = new Date().toDateString() === day.toDateString();

          return (
            <div key={idx} className="bg-white">
              <div className={`p-3 text-center border-b border-gray-100 ${
                isToday ? 'bg-[#FF6B35] text-white' : 'bg-gray-50/50'
              }`}>
                <div className="text-xs font-medium uppercase tracking-wide">
                  {day.toLocaleDateString('nl-NL', { weekday: 'short' })}
                </div>
                <div className={`text-lg font-semibold ${isToday ? 'text-white' : 'text-gray-900'}`}>
                  {day.getDate()}
                </div>
              </div>

              <div className="p-2 space-y-2 min-h-[380px]">
                {dayItems.map(item => {
                  const typeConfig = TYPE_CONFIG[item.type];
                  const FallbackIcon = item.type === 'social' ? getPlatformIcon(item.platform) : typeConfig.icon;

                  return (
                    <div
                      key={item.id}
                      className="group rounded-lg overflow-hidden shadow-sm border border-gray-100"
                    >
                      <div className="relative aspect-[4/3] overflow-hidden">
                        {item.imageUrl ? (
                          <img
                            src={item.imageUrl}
                            alt={item.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className={`w-full h-full bg-gradient-to-br ${typeConfig.gradient} flex items-center justify-center`}>
                            <FallbackIcon className="w-6 h-6 text-white/30" />
                          </div>
                        )}
                        <div className="absolute top-1.5 right-1.5">
                          {getStatusIcon(item.status)}
                        </div>
                      </div>
                      <div className="p-1.5">
                        <p className="text-[11px] text-gray-400 leading-none mb-0.5">
                          {item.date.toLocaleTimeString('nl-NL', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                        <p className="text-xs font-medium text-gray-900 line-clamp-2 leading-tight">
                          {item.title}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
